import { activarMutuo } from "../services/serviceBandejaMutuoLiquidacion";
import { MutuoSeleccionado } from "../models/MutuoSeleccionado";

export interface ActivacionMasivaProgress {
    total: number;
    procesados: number;
    exitosos: number;
    fallidos: number;
}

interface ActivacionMasivaOptions {
    concurrency?: number;
    onInit?: (resultadosIniciales: MutuoSeleccionado[], progress: ActivacionMasivaProgress) => void;
    onItemProcessed?: (mutuo: MutuoSeleccionado, resultadosActualizados: MutuoSeleccionado[], progress: ActivacionMasivaProgress) => void;
    onComplete?: (resultadosFinales: MutuoSeleccionado[], progress: ActivacionMasivaProgress) => void;
}

const crearProgress = (resultados: MutuoSeleccionado[]): ActivacionMasivaProgress => {
    const procesados = resultados.filter((item) => item.estadoActivacion === 'exitoso' || item.estadoActivacion === 'fallido').length;
    const exitosos = resultados.filter((item) => item.estadoActivacion === 'exitoso').length;
    const fallidos = resultados.filter((item) => item.estadoActivacion === 'fallido').length;

    return {
        total: resultados.length,
        procesados,
        exitosos,
        fallidos,
    };
};

const obtenerMensajeError = (error: any) => {
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }

    if (error?.message) {
        return error.message;
    }

    return "Error desconocido";
};

export function UseBandejaMutuos(){
    
    const activarMutuos = async (mutuosToActivate: MutuoSeleccionado[], options?: ActivacionMasivaOptions) => {
        const resultadosActivacion: MutuoSeleccionado[] = mutuosToActivate.map((mutuo, index) => ({
            ...mutuo,
            ordenActivacion: index + 1,
            estadoActivacion: 'procesando',
            observacion: 'Procesando activación...'
        }));

        options?.onInit?.([...resultadosActivacion], crearProgress(resultadosActivacion));

        const maxConcurrency = Math.max(1, Math.min(options?.concurrency ?? 4, resultadosActivacion.length));
        let currentIndex = 0;

        const processItem = async () => {
            while (true) {
                const indexToProcess = currentIndex;
                currentIndex += 1;

                if (indexToProcess >= resultadosActivacion.length) {
                    return;
                }

                const mutuo = resultadosActivacion[indexToProcess];

                try {
                    const response = await activarMutuo(mutuo.id, true, false);

                    if (response?.status === 200) {
                        resultadosActivacion[indexToProcess] = {
                            ...mutuo,
                            estadoActivacion: 'exitoso',
                            observacion: `El mutuo ha sido activado correctamente. El número de operación asociado es ${response?.data?.numeroOperacion}.`
                        };
                    } else {
                        resultadosActivacion[indexToProcess] = {
                            ...mutuo,
                            estadoActivacion: 'fallido',
                            observacion: (response as any)?.response?.data?.message || (response as any)?.data?.message || "Error inesperado al activar el mutuo"
                        };
                    }
                } catch (error: any) {
                    resultadosActivacion[indexToProcess] = {
                        ...mutuo,
                        estadoActivacion: 'fallido',
                        observacion: obtenerMensajeError(error)
                    };
                }

                const progress = crearProgress(resultadosActivacion);
                options?.onItemProcessed?.(resultadosActivacion[indexToProcess], [...resultadosActivacion], progress);
            }
        };

        await Promise.all(Array.from({ length: maxConcurrency }, () => processItem()));

        const finalProgress = crearProgress(resultadosActivacion);
        options?.onComplete?.([...resultadosActivacion], finalProgress);

        return resultadosActivacion;
    };

    const activarMutuoIndividual = async (mutuoId: number, ignorePrecioVenta: boolean) => {
        try{
            const response = await activarMutuo(mutuoId, ignorePrecioVenta, true);
            return response;
        }
        catch(e: any){
            return e;
        }
    }

    return {
        activarMutuos,
        activarMutuoIndividual
    };
    
};